import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FixedSizeList as List, type ListChildComponentProps } from "react-window";
import {
  ArrowUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  ExternalLink,
  FastForward,
  Filter,
  Mail,
  RefreshCw,
  Rewind,
  Search,
  Sparkles,
  X,
} from "lucide-react";

import CapabilityGate from "@/components/CapabilityGate";
import ManualContactCard from "@/components/crm/ManualContactCard";
import MergeContactDrawer from "@/components/crm/MergeContactDrawer";
import { useToast } from "@/components/ToastProvider";
import { EmailDrawer } from "@/components/email";
import { runDeduplication } from "@/api/approvals";
import {
  createLead,
  createContact,
  deleteContact,
  enrichCrmContact,
  enrichAllCrmContacts,
  fetchContact,
  fetchContactActivity,
  fetchContacts,
  fetchLeads,
  updateContact,
  type UpdateContactPayload,
  type CrmActivityLog,
} from "@/api/crm";
import { fetchEmailLogs, linkEmailContact, type EmailLog } from "@/api/email";
import { fetchAutopilotTriggers, type AutopilotTrigger } from "@/api/autopilot";
import { approveHitlItem, fetchHitlInbox } from "@/api/hitl";
import { approveApproval, getApprovals, rejectApproval } from "@/api/approvals";
import { fetchEpisodes, type MemoryEpisode } from "@/api/memory";
import { request } from "@/api/client";
import type { CrmContact, CrmLead } from "@/types/crm";
import type { CRMEventRecord } from "@/hooks/useCRMEvents";
import { formatDate } from "@/lib/crmUtils";
import type { HitlItem } from "@/types/hitl";
import HitlBadge from "@/components/hitl/HitlBadge";
import { HitlDetailDrawer } from "@/components/hitl/HitlDetailDrawer";
import { TimelineSummary, type TimelineSummaryEntry } from "@/components/observability/TimelineSummary";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerDescription,
  DrawerTitle,
} from "@/components/ui/drawer";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useEventStream } from "@/hooks/useEventStream";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useArtistScope } from "@/lib/artistContext";

const SEARCH_FILTERS = [
  { value: "name", label: "Name", placeholder: "Search by name, venue, or email" },
  { value: "tags", label: "Tags", placeholder: "Search tags" },
  { value: "state", label: "State" },
  { value: "type", label: "Type" },
] as const;

type SearchFilter = (typeof SEARCH_FILTERS)[number]["value"];

const initialContactsState = {
  data: [] as CrmContact[],
  loading: false,
  error: null as string | null,
  total: 0,
};
const initialLeadsState = { data: [] as CrmLead[], loading: false, error: null as string | null };
const CONTACT_ACTIONS = [
  { id: "create_lead", label: "Create booking lead" },
  { id: "send_email", label: "Send email" },
  { id: "enrich_contacts", label: "Enrich contacts (Web search)" },
  { id: "dedupe_contacts", label: "Run CRM dedupe" },
  { id: "dedup_workspace", label: "Check for Duplicates" },
  { id: "delete_contacts", label: "Delete Contact" },
] as const;

const CONTACT_TYPES = ["Person", "Organization", "Venue", "Media", "Other"] as const;
const ROLE_OPTIONS = ["BOOKER", "OWNER", "MANAGER", "PROMOTER", "ARTIST", "OTHER"] as const;
const STATE_OPTIONS = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];
const STAGE_OPTIONS = ["New", "Engaged", "Qualified", "Dormant"] as const;
const CONTACTS_PER_PAGE = 20;
const CONTACT_ROW_HEIGHT = 52;
const CONTACT_LIST_MAX_HEIGHT = 56 * 16;
const CRMInsightsPanel = lazy(() =>
  import("@/components/crm/CRMInsightsPanel").then((mod) => ({ default: mod.CRMInsightsPanel })),
);
const SORT_KEYS = [
  { value: "relevance", label: "Relevance" },
  { value: "updated_at", label: "Last updated" },
  { value: "name", label: "Name" },
  { value: "company", label: "Company" },
  { value: "email", label: "Email" },
  { value: "city", label: "City" },
  { value: "score", label: "Score" },
] as const;
type SortKey = (typeof SORT_KEYS)[number]["value"];
type SortOrder = "asc" | "desc";

const stripHtml = (value: string) =>
  value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const HITL_CONTACT_KEYS = ["contact_id", "crm_contact_id", "venue_id", "contactId"];
const HITL_LEAD_KEYS = ["lead_id", "crm_lead_id", "leadId"];

type TimelineSource = "crm_event" | "memory_episode" | "email_log" | "autopilot_trigger";
const BRACKET_TOKEN_REGEX = /\[([a-zA-Z0-9._-]+)\]/g;
const MUSTACHE_TOKEN_REGEX = /{{\s*([^}]+)\s*}}/g;

function toNumericId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
}

function extractIdsFromSource(
  source: Record<string, unknown> | null | undefined,
  keys: string[],
): number[] {
  if (!source) return [];
  const ids: number[] = [];
  keys.forEach((key) => {
    const candidate = toNumericId(source[key]);
    if (candidate !== null) {
      ids.push(candidate);
    }
  });
  return ids;
}

function gatherHitlIds(item: HitlItem, keys: string[]) {
  const seen = new Set<number>();
  [item.metadata as Record<string, unknown> | null, item.payload, item.context].forEach((block) => {
    extractIdsFromSource(block, keys).forEach((value) => {
      if (!seen.has(value)) seen.add(value);
    });
  });
  return Array.from(seen);
}

function formatTimelineEntry(
  params: {
    id?: string;
    timestamp?: string | null;
    eventType: string;
    summary: string;
    agent?: string | null;
    thread?: string | null;
  },
  source: TimelineSource,
): TimelineSummaryEntry {
  return {
    id: params.id ? `${source}:${params.id}` : undefined,
    timestamp: params.timestamp,
    eventType: params.eventType,
    summary: params.summary,
    agent: params.agent,
    thread: params.thread,
  };
}

function summarizeCrmEvent(record: CRMEventRecord): string {
  const payload = record.payload ?? {};
  if (typeof payload.summary === "string") return payload.summary;
  if (typeof payload.description === "string") return payload.description;
  if (typeof payload.title === "string") return payload.title;
  const fallback = JSON.stringify(payload);
  return fallback.length > 280 ? `${fallback.slice(0, 277)}…` : fallback;
}

function summarizeEmailLog(log: EmailLog): string {
  const opened = log.opened_at ? `Opened ${new Date(log.opened_at).toLocaleString()}` : "Not opened";
  const status = log.status ? log.status.replace("_", " ") : "pending";
  return `${status} • ${log.subject ?? "Email"} • ${opened}`;
}

function summarizeAutopilotTrigger(trigger: AutopilotTrigger): string {
  const workflow = trigger.workflow_id ?? "workflow";
  const status = trigger.status;
  return `${workflow} (${status}) triggered by ${trigger.event_type}`;
}

function crmEventMatchesContact(record: CRMEventRecord, contact: CrmContact, leadIds: number[]): boolean {
  const payload = record.payload ?? {};
  const contactMatches = extractIdsFromSource(payload, HITL_CONTACT_KEYS).some((id) => id === contact.id);
  if (contactMatches) return true;
  const leadMatches = extractIdsFromSource(payload, HITL_LEAD_KEYS).some((id) => leadIds.includes(id));
  if (leadMatches) return true;
  if (contact.email && typeof payload.email === "string" && payload.email.toLowerCase() === contact.email.toLowerCase()) {
    return true;
  }
  return false;
}

function autopilotMatchesContact(
  trigger: AutopilotTrigger,
  contact: CrmContact,
  leadIds: number[],
): boolean {
  const payload = trigger.event?.payload ?? {};
  const contactMatches = extractIdsFromSource(payload, HITL_CONTACT_KEYS).some((id) => id === contact.id);
  if (contactMatches) return true;
  const leadMatches = extractIdsFromSource(payload, HITL_LEAD_KEYS).some((id) => leadIds.includes(id));
  if (leadMatches) return true;
  if (contact.email && typeof payload.email === "string" && payload.email.toLowerCase() === contact.email.toLowerCase()) {
    return true;
  }
  return false;
}

function buildReplacementMap(contact?: CrmContact | null, lead?: CrmLead | null) {
  const map: Record<string, string> = {};
  if (contact) {
    map["contact.name"] = contact.name ?? contact.contact_name ?? "";
    map["contact.email"] = contact.email ?? "";
    map["contact.phone"] = contact.phone ?? "";
    map["contact.company"] = contact.company_name ?? "";
    map["contact.city"] = contact.location_city ?? "";
    map["contact.state"] = contact.location_region ?? "";
    map["contact.country"] = contact.country ?? "";
  }
  if (lead) {
    map["lead.title"] = lead.title ?? "";
    map["lead.stage"] = lead.pipeline_stage ?? "";
    map["lead.region"] = lead.region ?? "";
  }
  return map;
}

function extractTemplateTokens(text?: string | null) {
  if (!text) return [];
  const set = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = BRACKET_TOKEN_REGEX.exec(text)) !== null) {
    if (match[1]) set.add(match[1]);
  }
  while ((match = MUSTACHE_TOKEN_REGEX.exec(text)) !== null) {
    if (match[1]) set.add(match[1].trim());
  }
  return Array.from(set);
}

type CapabilityBlockedError = {
  code?: string;
  status?: number;
  capability?: string;
  payload?: { capability?: string; error?: string } | null;
};

function isCapabilityBlockedError(error: unknown): error is CapabilityBlockedError {
  if (!error || typeof error !== "object") return false;
  if ((error as CapabilityBlockedError).code === "capability_required") return true;
  if ((error as CapabilityBlockedError).status === 403) {
    const payload = (error as CapabilityBlockedError).payload;
    if (payload && payload.error === "requires_pro") return true;
  }
  return false;
}

function capabilityFromError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const direct = (error as CapabilityBlockedError).capability;
  if (typeof direct === "string" && direct) return direct;
  const payload = (error as CapabilityBlockedError).payload;
  if (payload && typeof payload.capability === "string" && payload.capability) {
    return payload.capability;
  }
  return null;
}

type ContactEditorState = {
  name: string;
  contact_name: string;
  company_name: string;
  contact_type: (typeof CONTACT_TYPES)[number];
  role_id: (typeof ROLE_OPTIONS)[number];
  crm_stage: (typeof STAGE_OPTIONS)[number];
  email: string;
  phone: string;
  website: string;
  location_city: string;
  location_region: string;
  country: string;
  address_line: string;
  tags: string;
  source_url: string;
  owner: string;
  notes: string;
};

const createContactEditorState = (contact?: CrmContact | null): ContactEditorState => ({
  name: contact?.name ?? "",
  contact_name: contact?.contact_name ?? "",
  company_name: contact?.company_name ?? "",
  contact_type: contact?.contact_type ?? CONTACT_TYPES[0],
  role_id: contact?.role_id ?? ROLE_OPTIONS[0],
  crm_stage: contact?.crm_stage ?? STAGE_OPTIONS[0],
  email: contact?.email ?? "",
  phone: contact?.phone ?? "",
  website: contact?.website ?? "",
  location_city: contact?.location_city ?? "",
  location_region: contact?.location_region ?? "",
  country: contact?.country ?? "",
  address_line: contact?.address_line ?? "",
  tags: (contact?.tags ?? []).join(", "),
  source_url: contact?.source_url ?? "",
  owner: contact?.owner ?? "",
  notes: contact?.notes ?? "",
});

export default function CrmPage() {
  const [contactsState, setContactsState] = useState(initialContactsState);
  const [leadsState, setLeadsState] = useState(initialLeadsState);
  const [contactSearch, setContactSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState<SearchFilter>(SEARCH_FILTERS[0].value);
  const [sortKey, setSortKey] = useState<SortKey>("relevance");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  const selectedContactIdRef = useRef<number | null>(null);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set());
  const [inlineEditId, setInlineEditId] = useState<number | null>(null);
  const [inlineDraft, setInlineDraft] = useState<{ name: string; email: string; url: string } | null>(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [undoEdit, setUndoEdit] = useState<{ contactId: number; prev: CrmContact; timeoutId: number } | null>(null);
  const inlineSaveTokenRef = useRef<number | null>(null);
  const [actionUndo, setActionUndo] = useState<
    | { type: "delete"; contacts: CrmContact[]; timeoutId: number }
    | { type: "merge"; primaryPrev: CrmContact; secondaryPrev: Array<{ id: number; notes?: string | null }>; timeoutId: number }
    | null
  >(null);
  const masterSelectRef = useRef<HTMLInputElement | null>(null);
  const [stateFilter, setStateFilter] = useState("");
  const [contactTypeFilter, setContactTypeFilter] = useState<"all" | "artists" | "venues">("all");
  const [contactPage, setContactPage] = useState(1);
  const [contactPageInput, setContactPageInput] = useState("1");
  const contactPageRef = useRef(1);
  const contactsLoadSeqRef = useRef(0);
  const lastArtistIdRef = useRef<string | null>(null);
  const lastSearchParamsRef = useRef<string>("");
  const filtersInitializedRef = useRef(false);
  const contactSearchRef = useRef("");
  const searchFilterRef = useRef<SearchFilter>(SEARCH_FILTERS[0].value);
  const [urlReady, setUrlReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [hitlApprovalId, setHitlApprovalId] = useState<string | null>(null);
  const sortKeyRef = useRef<SortKey>("relevance");
  const sortOrderRef = useRef<SortOrder>("desc");
  const createContactSeedRef = useRef<string>("");
  const [columnWidths, setColumnWidths] = useState({
    name: 260,
    location: 180,
    email: 220,
    url: 240,
  });
  const resizingRef = useRef<{ key: keyof typeof columnWidths; startX: number; startWidth: number } | null>(null);
  const lastSearchRef = useRef<{ value: string; filter: SearchFilter }>({
    value: "",
    filter: SEARCH_FILTERS[0].value,
  });

  const [dedupStatus, setDedupStatus] = useState<"running" | "queued" | "failed" | null>(null);
  const [dedupWorkspaceOpen, setDedupWorkspaceOpen] = useState(false);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupGroups, setDedupGroups] = useState<
    Array<{
      id: string;
      org_id?: string | null;
      contacts: Array<{
        id: number;
        label?: string | null;
        email?: string | null;
        phone?: string | null;
        website?: string | null;
        city?: string | null;
        state?: string | null;
        country?: string | null;
      }>;
      matches: Array<{ left_id: number; right_id: number; score: number; reasons?: string[] }>;
      auto_merge_candidate?: boolean;
    }>
  >([]);
  const [dedupIndex, setDedupIndex] = useState(0);
  const [dedupView, setDedupView] = useState<"single" | "table">("single");
  const [dedupSelection, setDedupSelection] = useState<Record<string, number>>({});
  const { push } = useToast();
  const { capabilities, openUpgradeModal } = useCapabilities();
  const { selectedArtistId, isLoading: artistLoading } = useArtistScope();
  const artistReady = Boolean(selectedArtistId) && !artistLoading;
  const contactsLoading = contactsState.loading || !urlReady || !artistReady;
  const canUseCrmInsights = Boolean(capabilities.canUseCrmInsights);
  const canUseCrmDedupe = Boolean(capabilities.canUseCrmDedupe);
  const canUseCrmGraphLinks = Boolean(capabilities.canUseCrmGraphLinks);
  const canUseLeadScoring = Boolean(capabilities.canUseLeadScoring);
  const canUseCrmLeadAutomation = Boolean(capabilities.canUseCrmLeadAutomation ?? capabilities.canUseLeadScoring);
  const canUseCrmEventAutomation = Boolean(capabilities.canUseCrmEventAutomation ?? capabilities.canUseLeadScoring);
  const handleCapabilityBlock = useCallback(
    (error: unknown, fallback?: string) => {
      if (!isCapabilityBlockedError(error)) return;
      const capability = capabilityFromError(error);
      openUpgradeModal(capability ?? fallback ?? "CRM Pro");
    },
    [openUpgradeModal],
  );
  const [selectedAction, setSelectedAction] = useState<(typeof CONTACT_ACTIONS)[number]["id"]>(CONTACT_ACTIONS[0].id);
  const [contactForm, setContactForm] = useState<ContactEditorState>(createContactEditorState());
  const [contactSaving, setContactSaving] = useState(false);
  const [contactSaveMessage, setContactSaveMessage] = useState<string | null>(null);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);
  const [emailDrawerContact, setEmailDrawerContact] = useState<CrmContact | null>(null);
  const [emailDrawerLead, setEmailDrawerLead] = useState<CrmLead | null>(null);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [createContactDrawerOpen, setCreateContactDrawerOpen] = useState(false);
  const [createContactInitial, setCreateContactInitial] = useState<{
    display_name?: string;
    company_name?: string;
    email?: string;
    phone?: string;
    url?: string;
    source_url?: string;
    notes?: string;
  } | null>(null);
  const [createContactLogId, setCreateContactLogId] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [hitlItems, setHitlItems] = useState<HitlItem[]>([]);
  const [hitlLoading, setHitlLoading] = useState(false);
  const [activeHitlId, setActiveHitlId] = useState<string | null>(null);
  const [activeHitlItem, setActiveHitlItem] = useState<HitlItem | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<TimelineSummaryEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [contactTab, setContactTab] = useState<"profile" | "timeline" | "history">("profile");
  const [mergeDrawerOpen, setMergeDrawerOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState<CrmActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const topBookingScore = useMemo(() => {
    const scored = contactsState.data.filter((contact) => typeof contact.last_booking_score === "number");
    if (!scored.length) return null;
    return scored.reduce((acc, contact) => {
      const score = Number(contact.last_booking_score);
      if (score > acc.score) {
        return { id: contact.id, score };
      }
      return acc;
    }, { id: scored[0].id, score: Number(scored[0].last_booking_score) });
  }, [contactsState.data]);

  const totalContactPages = useMemo(() => {
    const total = Math.ceil(contactsState.total / CONTACTS_PER_PAGE);
    return total > 0 ? total : 1;
  }, [contactsState.total]);

  const listHeight = useMemo(() => {
    const rows = Math.max(contactsState.data.length, 1);
    return Math.min(CONTACT_LIST_MAX_HEIGHT, rows * CONTACT_ROW_HEIGHT);
  }, [contactsState.data.length]);

  const loadContacts = useCallback(
    async (
      search?: string,
      filter?: SearchFilter,
      pageOverride?: number,
      resetPage = false,
    ) => {
      if (!artistReady) return;
      const requestSeq = ++contactsLoadSeqRef.current;
      setContactsState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        if (search !== undefined) {
          lastSearchRef.current = { ...lastSearchRef.current, value: search };
        }
        if (filter !== undefined) {
          lastSearchRef.current = { ...lastSearchRef.current, filter };
        }
        const targetPage = resetPage ? 1 : pageOverride ?? contactPageRef.current;
        const effectiveSearch = (lastSearchRef.current.value ?? "").trim();
        const tagFilter =
          contactTypeFilter === "artists" ? "artist" : contactTypeFilter === "venues" ? "bandsintown" : undefined;
        const effectiveFilter = lastSearchRef.current.filter;
        const queryParam = effectiveFilter === "name" ? effectiveSearch || undefined : undefined;
        const tagSearch =
          effectiveFilter === "tags" && effectiveSearch ? effectiveSearch : undefined;
        const combinedTags = [tagFilter, tagSearch].filter(Boolean).join(",");
        const payload = await fetchContacts({
          query: queryParam,
          region: stateFilter || undefined,
          tags: combinedTags || undefined,
          sort: sortKeyRef.current,
          order: sortOrderRef.current,
          page: targetPage,
          limit: CONTACTS_PER_PAGE,
        });
        if (requestSeq !== contactsLoadSeqRef.current) return;
        setContactsState({
          data: payload.contacts,
          total: payload.total ?? payload.contacts.length,
          loading: false,
          error: null,
        });
        setContactPage(targetPage);
        setContactPageInput(String(targetPage));
        if (payload.contacts.length) {
          const currentSelected = selectedContactIdRef.current;
          if (currentSelected === null || !payload.contacts.some((contact) => contact.id === currentSelected)) {
            setSelectedContactId(payload.contacts[0].id);
          }
        }
      } catch (error) {
        if (requestSeq !== contactsLoadSeqRef.current) return;
        setContactsState((prev) => ({ ...prev, loading: false, error: (error as Error)?.message ?? "Unable to load contacts." }));
      }
    },
    [stateFilter, contactTypeFilter, artistReady],
  );

  const goToContactPage = useCallback(
    (page: number) => {
      const next = Math.min(Math.max(page, 1), totalContactPages);
      if (!Number.isFinite(next)) return;
      if (next === contactPage) {
        setContactPageInput(String(contactPage));
        return;
      }
      contactPageRef.current = next;
      setContactPage(next);
      setContactPageInput(String(next));
      void loadContacts(undefined, undefined, next);
    },
    [contactPage, loadContacts, totalContactPages],
  );

  const commitContactPageInput = useCallback(
    (rawValue: string) => {
      const next = Number(rawValue);
      if (!Number.isFinite(next) || next < 1) {
        setContactPageInput(String(contactPage));
        return;
      }
      goToContactPage(next);
    },
    [contactPage, goToContactPage],
  );

  const loadLeads = useCallback(async () => {
    setLeadsState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchLeads({ status: "open" });
      setLeadsState({ data, loading: false, error: null });
    } catch (error) {
      setLeadsState((prev) => ({ ...prev, loading: false, error: (error as Error)?.message ?? "Unable to load leads." }));
    }
  }, []);

  useEffect(() => {
    const paramString = searchParams.toString();
    if (urlReady && paramString === lastSearchParamsRef.current) {
      return;
    }
    const initialSearch = searchParams.get("q") ?? "";
    const initialFilter = (searchParams.get("filter") as SearchFilter) ?? SEARCH_FILTERS[0].value;
    const initialState = searchParams.get("state") ?? "";
    const initialType = (searchParams.get("type") as "all" | "artists" | "venues") ?? "all";
    const initialPage = Number(searchParams.get("page") ?? "1");
    const initialSort = (searchParams.get("sort") as SortKey) ?? "relevance";
    const initialOrder = (searchParams.get("order") as SortOrder) ?? "desc";
    const initialSelected = Number(searchParams.get("contact") ?? "");
    const initialHitl = searchParams.get("hitl");
    const newContact = searchParams.get("newContact");
    const newContactEmail = searchParams.get("email") ?? "";
    const newContactName = searchParams.get("name") ?? "";
    const newContactCompany = searchParams.get("company") ?? "";
    const newContactPhone = searchParams.get("phone") ?? "";
    const newContactUrl = searchParams.get("url") ?? "";
    const newContactSource = searchParams.get("source") ?? "";
    const newContactToken = searchParams.get("token") ?? "";
    const newContactLogId = searchParams.get("logId") ?? "";

    const validFilter = SEARCH_FILTERS.some((item) => item.value === initialFilter)
      ? initialFilter
      : SEARCH_FILTERS[0].value;
    const validType = ["all", "artists", "venues"].includes(initialType) ? initialType : "all";
    const nextPage = Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1;
    const validSort = SORT_KEYS.some((item) => item.value === initialSort) ? initialSort : "relevance";
    const validOrder = initialOrder === "asc" || initialOrder === "desc" ? initialOrder : "desc";

    setContactSearch(initialSearch);
    setSearchFilter(validFilter);
    setStateFilter(initialState);
    setContactTypeFilter(validType);
    setContactPage(nextPage);
    setContactPageInput(String(nextPage));
    setSortKey(validSort);
    setSortOrder(validOrder);
    if (Number.isFinite(initialSelected) && initialSelected > 0) {
      setSelectedContactId(initialSelected);
    }
    if (initialHitl) {
      setHitlApprovalId(initialHitl);
    } else {
      setHitlApprovalId(null);
    }

    if (newContact === "1") {
      const seed = [
        newContactEmail,
        newContactName,
        newContactCompany,
        newContactPhone,
        newContactUrl,
        newContactSource,
        newContactToken,
        newContactLogId,
      ].join("|");
      if (seed !== createContactSeedRef.current) {
        createContactSeedRef.current = seed;
        const sourceUrl = newContactSource
          ? newContactSource.startsWith("email:")
            ? newContactSource
            : `email:${newContactSource}`
          : undefined;
        setCreateContactInitial({
          display_name: newContactName || undefined,
          company_name: newContactCompany || undefined,
          email: newContactEmail || undefined,
          phone: newContactPhone || undefined,
          url: newContactUrl || undefined,
          source_url: sourceUrl,
          notes: newContactSource ? `Source: ${newContactSource}` : undefined,
        });
        setCreateContactLogId(newContactLogId || null);
        setCreateContactDrawerOpen(true);
      }
    }
    lastSearchRef.current = { value: initialSearch, filter: validFilter };
    contactPageRef.current = nextPage;
    contactSearchRef.current = initialSearch;
    searchFilterRef.current = validFilter;
    sortKeyRef.current = validSort;
    sortOrderRef.current = validOrder;
    lastSearchParamsRef.current = paramString;

    if (!urlReady) setUrlReady(true);
  }, [searchParams, urlReady]);

  useEffect(() => {
    if (!urlReady || !artistReady) return;
    const artistChanged = selectedArtistId && selectedArtistId !== lastArtistIdRef.current;
    if (artistChanged) {
      lastArtistIdRef.current = selectedArtistId;
      contactPageRef.current = 1;
      setContactPage(1);
      setContactPageInput("1");
      void loadContacts(contactSearchRef.current, searchFilterRef.current, 1, true);
    } else {
      void loadContacts(contactSearchRef.current, searchFilterRef.current, contactPageRef.current);
    }
    loadLeads();
  }, [urlReady, artistReady, selectedArtistId, loadContacts, loadLeads]);

  useEffect(() => {
    contactPageRef.current = contactPage;
  }, [contactPage]);

  useEffect(() => {
    selectedContactIdRef.current = selectedContactId;
  }, [selectedContactId]);

  useEffect(() => {
    contactSearchRef.current = contactSearch;
  }, [contactSearch]);

  useEffect(() => {
    searchFilterRef.current = searchFilter;
  }, [searchFilter]);

  useEffect(() => {
    sortKeyRef.current = sortKey;
  }, [sortKey]);

  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  useEffect(() => {
    if (!urlReady) return;
    const nextParams: Record<string, string> = {};
    if (contactSearch) nextParams.q = contactSearch;
    if (searchFilter !== SEARCH_FILTERS[0].value) nextParams.filter = searchFilter;
    if (stateFilter) nextParams.state = stateFilter;
    if (contactTypeFilter !== "all") nextParams.type = contactTypeFilter;
    if (contactPage > 1) nextParams.page = String(contactPage);
    if (sortKey !== "relevance") nextParams.sort = sortKey;
    if (sortOrder !== "desc") nextParams.order = sortOrder;
    if (selectedContactId) nextParams.contact = String(selectedContactId);
    if (hitlApprovalId) nextParams.hitl = hitlApprovalId;
    const paramString = new URLSearchParams(nextParams).toString();
    const currentParams = searchParams.toString();
    if (paramString === currentParams) return;
    lastSearchParamsRef.current = paramString;
    setSearchParams(nextParams, { replace: true });
  }, [
    contactSearch,
    searchFilter,
    stateFilter,
    contactTypeFilter,
    contactPage,
    sortKey,
    sortOrder,
    selectedContactId,
    hitlApprovalId,
    setSearchParams,
    searchParams,
    urlReady,
  ]);

  useEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true;
      return;
    }
    void loadContacts(contactSearchRef.current, searchFilterRef.current, 1, true);
  }, [contactTypeFilter, stateFilter, loadContacts]);

  useEffect(() => {
    setSelectedContactIds((prev) => {
      const availableIds = new Set(contactsState.data.map((contact) => contact.id));
      const next = new Set([...prev].filter((id) => availableIds.has(id)));
      return next;
    });
  }, [contactsState.data]);

  useEffect(() => {
    if (contactPage > totalContactPages) {
      void loadContacts(undefined, undefined, totalContactPages);
    }
  }, [contactPage, loadContacts, totalContactPages]);

  useEffect(() => {
    if (!contactsState.data.length) return;
    if (!selectedContactId || !contactsState.data.some((contact) => contact.id === selectedContactId)) {
      setSelectedContactId(contactsState.data[0].id);
    }
  }, [contactsState.data, selectedContactId]);

  useEffect(() => {
    if (!hitlApprovalId) return;
    if (!selectedContactId) return;
    if (!contactDrawerOpen) {
      setContactDrawerOpen(true);
    }
  }, [hitlApprovalId, selectedContactId, contactDrawerOpen]);

  const selectedContact = useMemo(
    () => contactsState.data.find((contact) => contact.id === selectedContactId) ?? null,
    [contactsState.data, selectedContactId],
  );

  const leadsForContact = useMemo(() => {
    if (!selectedContact) return [];
    return leadsState.data.filter((lead) => lead.contact_id === (selectedContact?.id ?? -1));
  }, [leadsState.data, selectedContact]);

  useEffect(() => {
    if (contactTab !== "history" || !selectedContact) return;
    let cancelled = false;
    setActivityLoading(true);
    setActivityError(null);
    fetchContactActivity(selectedContact.id, 50)
      .then((payload) => {
        if (cancelled) return;
        setActivityLogs(payload ?? []);
      })
      .catch((error) => {
        if (cancelled) return;
        setActivityError(error instanceof Error ? error.message : "Unable to load activity history.");
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactTab, selectedContact]);

  const leadByContactId = useMemo(() => {
    const map = new Map<number, CrmLead>();
    leadsState.data.forEach((lead) => {
      if (lead.contact_id != null && !map.has(lead.contact_id)) {
        map.set(lead.contact_id, lead);
      }
    });
    return map;
  }, [leadsState.data]);

  const activeSearchFilter =
    SEARCH_FILTERS.find((filter) => filter.value === searchFilter) ?? SEARCH_FILTERS[0];
  const showSearchInputFilter = searchFilter === "name" || searchFilter === "tags";
  const showStateDropdown = searchFilter === "state";
  const showTypeDropdown = searchFilter === "type";

  useEffect(() => {
    setContactForm(createContactEditorState(selectedContact));
    setContactSaveMessage(null);
  }, [selectedContact?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.type) return;
      if (detail.type === "crm:create-contact") {
        setCreateContactDrawerOpen(true);
      }
    };
    window.addEventListener("command-palette", handler as EventListener);
    return () => {
      window.removeEventListener("command-palette", handler as EventListener);
    };
  }, []);


  const insightTarget = useMemo(() => {
    if (leadsForContact.length) {
      const lead = leadsForContact[0];
      return { type: "lead" as const, id: lead.id, name: lead.title ?? `Lead #${lead.id}` };
    }
    if (selectedContact) {
      return {
        type: "venue" as const,
        id: selectedContact.id,
        name: selectedContact.name || selectedContact.company_name || `Contact #${selectedContact.id}`,
      };
    }
    return { type: "venue" as const, id: null, name: null };
  }, [leadsForContact, selectedContact]);

  const startInlineEdit = useCallback((contact: CrmContact) => {
    setInlineEditId(contact.id);
    setInlineDraft({
      name: contact.name ?? "",
      email: contact.email ?? "",
      url: contact.website ?? contact.source_url ?? contact.source_urls?.[0] ?? "",
    });
  }, []);

  const cancelInlineEdit = useCallback(() => {
    setInlineEditId(null);
    setInlineDraft(null);
  }, []);

  const saveInlineEdit = useCallback(async () => {
    if (!inlineEditId || !inlineDraft) return;
    const previous = contactsState.data.find((contact) => contact.id === inlineEditId);
    if (!previous) return;
    const payload: UpdateContactPayload = {
      name: inlineDraft.name || undefined,
      email: inlineDraft.email || undefined,
      website: inlineDraft.url || undefined,
    };
    const optimistic = { ...previous, ...payload };
    setContactsState((prev) => ({
      ...prev,
      data: prev.data.map((contact) => (contact.id === inlineEditId ? optimistic : contact)),
    }));
    const token = Date.now();
    inlineSaveTokenRef.current = token;
    if (undoEdit?.timeoutId) clearTimeout(undoEdit.timeoutId);
    const timeoutId = window.setTimeout(() => {
      setUndoEdit(null);
    }, 8000);
    setUndoEdit({ contactId: inlineEditId, prev: previous, timeoutId });
    setInlineSaving(true);
    try {
      const updated = await updateContact(inlineEditId, payload);
      if (inlineSaveTokenRef.current !== token) return;
      setContactsState((prev) => ({
        ...prev,
        data: prev.data.map((contact) => (contact.id === updated.id ? updated : contact)),
      }));
      setSelectedContactId(updated.id);
      push({ title: "Contact updated", message: `${updated.name || updated.company_name || "Contact"} saved.` });
      cancelInlineEdit();
    } catch (error) {
      if (inlineSaveTokenRef.current === token) {
        setContactsState((prev) => ({
          ...prev,
          data: prev.data.map((contact) => (contact.id === previous.id ? previous : contact)),
        }));
        setUndoEdit(null);
        push({ title: "Save failed", message: error instanceof Error ? error.message : "Unable to save contact." });
      }
    } finally {
      setInlineSaving(false);
    }
  }, [cancelInlineEdit, contactsState.data, inlineDraft, inlineEditId, push, undoEdit?.timeoutId]);

  const undoInlineEdit = useCallback(async () => {
    if (!undoEdit) return;
    if (undoEdit.timeoutId) clearTimeout(undoEdit.timeoutId);
    inlineSaveTokenRef.current = null;
    setContactsState((prev) => ({
      ...prev,
      data: prev.data.map((contact) => (contact.id === undoEdit.contactId ? undoEdit.prev : contact)),
    }));
    setUndoEdit(null);
    try {
      await updateContact(undoEdit.contactId, {
        name: undoEdit.prev.name || undefined,
        email: undoEdit.prev.email || undefined,
        website: undoEdit.prev.website || undefined,
      });
      push({ title: "Undo complete", message: "Contact changes reverted." });
    } catch (error) {
      push({ title: "Undo failed", message: error instanceof Error ? error.message : "Unable to revert contact." });
    }
  }, [push, undoEdit]);

  const handleCopy = useCallback(
    async (label: string, value?: string | null) => {
      if (!value) {
        push({ title: "Missing value", message: `No ${label} on file.` });
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        push({ title: "Copied", message: `${label} copied to clipboard.` });
      } catch (error) {
        push({ title: "Copy failed", message: error instanceof Error ? error.message : "Unable to copy value." });
      }
    },
    [push],
  );

  const handleQuickEmail = useCallback(
    (contact: CrmContact) => {
      setEmailDrawerContact(contact);
      const lead = contact.id ? leadByContactId.get(contact.id) ?? null : null;
      setEmailDrawerLead(lead);
      setEmailDrawerOpen(true);
    },
    [leadByContactId],
  );

  const handleQuickEnrich = useCallback(
    async (contact: CrmContact) => {
      try {
        await enrichCrmContact(contact.id);
        push({
          title: "Enrichment queued",
          message: `${contact.name || contact.company_name || "Contact"} enrichment requested.`,
        });
        await loadContacts(contactSearchRef.current, searchFilterRef.current, contactPageRef.current);
      } catch (error) {
        push({ title: "Enrichment failed", message: error instanceof Error ? error.message : "Unable to enrich contact." });
      }
    },
    [loadContacts, push],
  );

  const handleLeadAutomationAction = useCallback(
    (action: "score" | "tag" | "classify") => {
      if (!canUseCrmLeadAutomation) {
        openUpgradeModal("CRM lead automation");
        return;
      }
      const lead = leadsForContact[0];
      if (!lead) {
        push({ title: "No lead selected", message: "Link a lead to this contact before running automation." });
        return;
      }
      const labels: Record<typeof action, string> = {
        score: "Lead auto-score queued",
        tag: "AI contact tagging queued",
        classify: "AI lead classification queued",
      };
      push({ title: "Lead automation", message: `${labels[action]} for ${lead.title ?? `Lead #${lead.id}`}.` });
    },
    [canUseCrmLeadAutomation, leadsForContact, openUpgradeModal, push],
  );

  const handleEventAutomationAction = useCallback(
    (action: "create" | "calendar" | "attachVenue" | "attachContact") => {
      if (!canUseCrmEventAutomation) {
        openUpgradeModal("CRM event automation");
        return;
      }
      if (!selectedContact) {
        push({ title: "Select a contact", message: "Choose a contact first." });
        return;
      }
      const labels: Record<typeof action, string> = {
        create: "AI booking event draft created",
        calendar: "Calendar event queued",
        attachVenue: "Venue association queued",
        attachContact: "Contact metadata queued",
      };
      push({
        title: "Event automation",
        message: `${labels[action]} for ${selectedContact.name || selectedContact.company_name || `Contact #${selectedContact.id}`}.`,
      });
    },
    [canUseCrmEventAutomation, openUpgradeModal, push, selectedContact],
  );

  const contactLinks = useMemo(() => {
    if (!selectedContact) return [];
    const lead = leadsForContact[0] ?? null;
    const links = [
      { label: "Open in Booking", href: lead ? `/booking?leadId=${lead.id}` : "/booking" },
      { label: "Lead Detail", href: lead ? `/contacts/leads/${lead.id}` : "/contacts/leads" },
      { label: "Open in ManagerAgent", href: `/manager?contactId=${selectedContact.id}` },
      { label: "View in Routing", href: `/routing?contactId=${selectedContact.id}` },
      { label: "View Memory Records", href: `/memory/episodes?venue_id=${selectedContact.id}` },
      { label: "Promote Memory", href: `/memory?contact_id=${selectedContact.id}` },
    ];
    return links;
  }, [leadsForContact, selectedContact]);

  const contactsById = useMemo(() => {
    const map = new Map<number, CrmContact>();
    contactsState.data.forEach((contact) => map.set(contact.id, contact));
    return map;
  }, [contactsState.data]);

  const leadsById = useMemo(() => {
    const map = new Map<number, CrmLead>();
    leadsState.data.forEach((lead) => map.set(lead.id, lead));
    return map;
  }, [leadsState.data]);

  const hitlByContactId = useMemo(() => {
    const map = new Map<number, HitlItem>();
    hitlItems.forEach((item) => {
      gatherHitlIds(item, HITL_CONTACT_KEYS).forEach((id) => {
        if (!map.has(id)) {
          map.set(id, item);
        }
      });
    });
    return map;
  }, [hitlItems]);

  const hitlByLeadId = useMemo(() => {
    const map = new Map<number, HitlItem>();
    hitlItems.forEach((item) => {
      gatherHitlIds(item, HITL_LEAD_KEYS).forEach((id) => {
        if (!map.has(id)) {
          map.set(id, item);
        }
      });
    });
    return map;
  }, [hitlItems]);

  const activeDedupGroup = dedupGroups[dedupIndex] ?? null;

  const dedupFields = useMemo(
    () =>
      [
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "website", label: "Website" },
        { key: "city", label: "City" },
        { key: "state", label: "State" },
        { key: "country", label: "Country" },
      ] as const,
    [],
  );

  const loadDedupQueue = useCallback(async () => {
    setDedupLoading(true);
    try {
      const payload = await getApprovals(200);
      const items = Array.isArray(payload) ? payload : payload.items;
      const groups =
        items
          ?.map((item) => {
            const payload = item.payload as Record<string, unknown> | undefined | null;
            const payloadKind = typeof payload?.kind === "string" ? payload.kind : null;
            if (payloadKind !== "crm_contact_deduplication") return null;
            const contacts = Array.isArray(payload?.contacts) ? payload?.contacts : [];
            const matches = Array.isArray(payload?.matches) ? payload?.matches : [];
            return {
              id: item.id,
              org_id: (payload?.org_id as string | null | undefined) ?? null,
              contacts,
              matches,
              auto_merge_candidate: Boolean(payload?.auto_merge_candidate),
            };
          })
          .filter(Boolean) as typeof dedupGroups;
      setDedupGroups(groups);
      setDedupIndex((prev) => (groups.length ? Math.min(prev, groups.length - 1) : 0));
    } catch (error) {
      console.warn("Failed to load dedup queue", error);
    } finally {
      setDedupLoading(false);
    }
  }, []);

  useEffect(() => {
    if (dedupWorkspaceOpen) {
      void loadDedupQueue();
    }
  }, [dedupWorkspaceOpen, loadDedupQueue]);

  useEffect(() => {
    if (!dedupWorkspaceOpen || dedupStatus !== "queued") return;
    const interval = window.setInterval(() => {
      void loadDedupQueue();
    }, 5000);
    return () => window.clearInterval(interval);
  }, [dedupWorkspaceOpen, dedupStatus, loadDedupQueue]);

  useEffect(() => {
    if (!activeDedupGroup) {
      setDedupSelection({});
      return;
    }
    const contactScores = new Map<number, number>();
    activeDedupGroup.contacts.forEach((contact) => {
      const score =
        Number(Boolean(contact.label)) +
        Number(Boolean(contact.email)) +
        Number(Boolean(contact.phone)) +
        Number(Boolean(contact.website)) +
        Number(Boolean(contact.city)) +
        Number(Boolean(contact.state)) +
        Number(Boolean(contact.country));
      contactScores.set(contact.id, score);
    });
    const pickBest = (values: Array<{ id: number; value?: string | null }>) => {
      const sorted = values
        .filter((entry) => entry.value)
        .sort((a, b) => (contactScores.get(b.id) ?? 0) - (contactScores.get(a.id) ?? 0));
      return sorted[0]?.id;
    };
    const nextSelection: Record<string, number> = {};
    const defaultPrimary =
      activeDedupGroup.contacts
        .slice()
        .sort((a, b) => (contactScores.get(b.id) ?? 0) - (contactScores.get(a.id) ?? 0))[0]?.id ??
      activeDedupGroup.contacts[0]?.id;
    if (defaultPrimary) nextSelection.primary = defaultPrimary;
    nextSelection.name = pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.label }))) ?? defaultPrimary;
    nextSelection.email = pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.email }))) ?? defaultPrimary;
    nextSelection.phone = pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.phone }))) ?? defaultPrimary;
    nextSelection.website =
      pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.website }))) ?? defaultPrimary;
    nextSelection.city = pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.city }))) ?? defaultPrimary;
    nextSelection.state =
      pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.state }))) ?? defaultPrimary;
    nextSelection.country =
      pickBest(activeDedupGroup.contacts.map((c) => ({ id: c.id, value: c.country }))) ?? defaultPrimary;
    setDedupSelection(nextSelection);
  }, [activeDedupGroup]);

  const handleMergeContacts = useCallback(
    async ({ primaryId, updates, secondaryIds }: { primaryId: number; updates: Partial<CrmContact>; secondaryIds: number[] }) => {
      if (!canUseCrmDedupe) {
        openUpgradeModal("CRM dedupe");
        return;
      }
      const primaryPrev = contactsState.data.find((contact) => contact.id === primaryId);
      const secondaryPrev = contactsState.data
        .filter((contact) => secondaryIds.includes(contact.id))
        .map((contact) => ({ id: contact.id, notes: contact.notes }));
      try {
        const updated = await updateContact(primaryId, updates);
        setContactsState((prev) => ({
          ...prev,
          data: prev.data.map((contact) => (contact.id === updated.id ? updated : contact)),
        }));
        await Promise.all(
          secondaryIds.map((id) =>
            updateContact(id, {
              notes: `Merged into contact #${primaryId} on ${new Date().toLocaleDateString()}`,
            }),
          ),
        );
        if (primaryPrev) {
          if (actionUndo?.timeoutId) clearTimeout(actionUndo.timeoutId);
          const timeoutId = window.setTimeout(() => {
            setActionUndo(null);
          }, 8000);
          setActionUndo({ type: "merge", primaryPrev, secondaryPrev, timeoutId });
        }
        push({ title: "Merge approved", message: `Contact #${primaryId} now canonical. HITL notified.` });
      } catch (error) {
        push({ title: "Merge failed", message: error instanceof Error ? error.message : "Unable to merge contacts." });
        handleCapabilityBlock(error, "CRM dedupe");
      }
    },
    [actionUndo?.timeoutId, canUseCrmDedupe, contactsState.data, handleCapabilityBlock, openUpgradeModal, push],
  );

  const resolveDedupGroup = useCallback(
    async (action: "merge" | "skip") => {
      if (!activeDedupGroup) return;
      if (action === "skip") {
        try {
          await rejectApproval(activeDedupGroup.id, { reason: "Not a duplicate" });
        } catch (error) {
          console.warn("Failed to reject dedup item", error);
        }
      } else {
        const primaryId = dedupSelection.primary ?? activeDedupGroup.contacts[0]?.id;
        if (!primaryId) return;
        const primaryContact = activeDedupGroup.contacts.find((contact) => contact.id === primaryId);
        if (!primaryContact) return;
        const pickValue = (field: "label" | "email" | "phone" | "website" | "city" | "state" | "country") => {
          const id = dedupSelection[field === "label" ? "name" : field];
          const contact = activeDedupGroup.contacts.find((entry) => entry.id === id);
          return contact?.[field] ?? null;
        };
        const updates: Partial<CrmContact> = {
          name: pickValue("label") ?? undefined,
          email: pickValue("email") ?? undefined,
          phone: pickValue("phone") ?? undefined,
          website: pickValue("website") ?? undefined,
          location_city: pickValue("city") ?? undefined,
          location_region: pickValue("state") ?? undefined,
          country: pickValue("country") ?? undefined,
        };
        const secondaryIds = activeDedupGroup.contacts.filter((c) => c.id !== primaryId).map((c) => c.id);
        try {
          await handleMergeContacts({ primaryId, updates, secondaryIds });
          await approveApproval(activeDedupGroup.id);
        } catch (error) {
          console.warn("Failed to merge dedup group", error);
          push({ title: "Merge failed", message: error instanceof Error ? error.message : "Unable to merge contacts." });
          return;
        }
      }
      setDedupGroups((prev) => {
        const next = prev.filter((group) => group.id !== activeDedupGroup.id);
        setDedupIndex((current) => Math.min(current, Math.max(0, next.length - 1)));
        return next;
      });
    },
    [activeDedupGroup, dedupSelection, handleMergeContacts, push],
  );

  const getDedupValue = useCallback(
    (
      contact: {
        label?: string | null;
        email?: string | null;
        phone?: string | null;
        website?: string | null;
        city?: string | null;
        state?: string | null;
        country?: string | null;
      },
      key: (typeof dedupFields)[number]["key"],
    ) => {
      switch (key) {
        case "name":
          return contact.label ?? null;
        case "email":
          return contact.email ?? null;
        case "phone":
          return contact.phone ?? null;
        case "website":
          return contact.website ?? null;
        case "city":
          return contact.city ?? null;
        case "state":
          return contact.state ?? null;
        case "country":
          return contact.country ?? null;
        default:
          return null;
      }
    },
    [dedupFields],
  );

  const loadHitlQueue = useCallback(async () => {
    setHitlLoading(true);
    try {
      const items = await fetchHitlInbox({ status: "pending", category: "crm", limit: 200 });
      setHitlItems(items);
    } catch (error) {
      console.warn("Failed to load CRM HITL queue", error);
    } finally {
      setHitlLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHitlQueue();
  }, [loadHitlQueue]);

  const hitlEventHandler = useCallback(
    (event: { event: string }) => {
      if (event.event === "hitl_pending") {
        void loadHitlQueue();
      }
    },
    [loadHitlQueue],
  );

  useEventStream(hitlEventHandler);

  const loadContactTimeline = useCallback(
    async (contact: CrmContact | null, leads: CrmLead[]) => {
      if (!contact) {
        setTimelineEntries([]);
        setTimelineError(null);
        return;
      }
      setTimelineLoading(true);
      setTimelineError(null);
      const leadIds = leads.map((lead) => lead.id);
      try {
        const [eventsResult, episodesResult, emailLogsResult, triggersResult] = await Promise.allSettled([
          request<{ events: CRMEventRecord[] }>("/crm/events?limit=100"),
          fetchEpisodes({ venue_id: contact.id, lead_id: leadIds[0] ?? undefined, limit: 25 }),
          fetchEmailLogs({ contact_id: contact.id, limit: 100 }),
          fetchAutopilotTriggers({ limit: 100 }),
        ]);
        const events = eventsResult.status === "fulfilled" ? eventsResult.value?.events ?? [] : [];
        const episodes = episodesResult.status === "fulfilled" ? episodesResult.value ?? [] : [];
        const emailLogs = emailLogsResult.status === "fulfilled" ? emailLogsResult.value ?? [] : [];
        const triggers = triggersResult.status === "fulfilled" ? triggersResult.value?.items ?? [] : [];

        const entries: TimelineSummaryEntry[] = [];
        events
          .filter((event) => crmEventMatchesContact(event, contact, leadIds))
          .forEach((event) => {
            entries.push(
              formatTimelineEntry(
                {
                  id: event.id,
                  timestamp: event.timestamp,
                  eventType: event.event_type,
                  summary: summarizeCrmEvent(event),
                  agent: event.agent,
                  thread: event.thread_id,
                },
                "crm_event",
              ),
            );
          });
        episodes.forEach((episode: MemoryEpisode) => {
          entries.push(
            formatTimelineEntry(
              {
                id: episode.id,
                timestamp: episode.timestamp ?? undefined,
                eventType: "memory.episode",
                summary: episode.summary,
                agent: episode.agent,
              },
              "memory_episode",
            ),
          );
        });
        emailLogs.forEach((log) => {
          entries.push(
            formatTimelineEntry(
              {
                id: log.id,
                timestamp: log.created_at ?? log.sent_at ?? log.opened_at ?? undefined,
                eventType: `email.${log.status ?? "log"}`,
                summary: summarizeEmailLog(log),
              },
              "email_log",
            ),
          );
        });
        triggers
          .filter((trigger) => autopilotMatchesContact(trigger, contact, leadIds))
          .forEach((trigger) => {
            entries.push(
              formatTimelineEntry(
                {
                  id: trigger.id,
                  timestamp: trigger.timestamp,
                  eventType: `autopilot.${trigger.status}`,
                  summary: summarizeAutopilotTrigger(trigger),
                  thread: trigger.thread_id ?? undefined,
                },
                "autopilot_trigger",
              ),
            );
          });

        entries.sort((a, b) => {
          const aTime = a.timestamp ? Date.parse(a.timestamp) : 0;
          const bTime = b.timestamp ? Date.parse(b.timestamp) : 0;
          return bTime - aTime;
        });
        setTimelineEntries(entries.slice(0, 60));
      } catch (error) {
        setTimelineError(error instanceof Error ? error.message : "Unable to load timeline.");
      } finally {
        setTimelineLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void loadContactTimeline(selectedContact, leadsForContact);
  }, [selectedContact, leadsForContact, loadContactTimeline]);

  const openHitlDrawer = useCallback((item: HitlItem) => {
    setActiveHitlId(item.id);
    setActiveHitlItem(item);
  }, []);

  const closeHitlDrawer = useCallback(() => {
    setActiveHitlId(null);
    setActiveHitlItem(null);
  }, []);

  const handleEmailDrawerClose = useCallback(() => {
    setEmailDrawerOpen(false);
  }, []);

  const renderHitlPill = useCallback(
    (item: HitlItem | null | undefined, size: "sm" | "md" = "sm") => {
      if (!item) return null;
      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto rounded-full border border-transparent p-0 hover:border-primary/50"
          onClick={() => openHitlDrawer(item)}
        >
          <HitlBadge status={item.status} deadline={item.deadline} size={size} />
        </Button>
      );
    },
    [openHitlDrawer],
  );

  const handleAssociateBandsintown = useCallback(async () => {
    if (!selectedContact) {
      push({ title: "Select contact", message: "Choose a contact before linking Bandsintown data." });
      return;
    }
    const slug = window.prompt("Bandsintown venue slug or URL", "");
    if (!slug) return;
    const normalized = slug.includes("bandsintown.com")
      ? slug.trim()
      : `https://www.bandsintown.com/${slug.replace(/^\//, "")}`;
    try {
      const updated = await updateContact(selectedContact.id, {
        source_urls: Array.from(new Set([...(selectedContact.source_urls ?? []), normalized])),
        tags: Array.from(new Set([...(selectedContact.tags ?? []), "bandsintown"])),
      });
      setContactsState((prev) => ({
        ...prev,
        data: prev.data.map((contact) => (contact.id === updated.id ? updated : contact)),
      }));
      push({ title: "Bandsintown linked", message: "Venue association saved to CRM." });
    } catch (error) {
      push({
        title: "Association failed",
        message: error instanceof Error ? error.message : "Unable to update Bandsintown link.",
      });
      handleCapabilityBlock(error, "CRM ingestion");
    }
  }, [handleCapabilityBlock, push, selectedContact]);

  const handleDedup = async () => {
    if (!canUseCrmDedupe) {
      openUpgradeModal("CRM dedupe");
      return;
    }
    setDedupStatus("running");
    try {
      await runDeduplication({ source: "crm", limit: 1000 });
      setDedupStatus("queued");
      setDedupWorkspaceOpen(true);
    } catch (err) {
      setDedupStatus("failed");
      handleCapabilityBlock(err, "CRM dedupe");
    }
  };

  const handleContactFieldChange = (field: keyof ContactEditorState, value: string) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleContactSave = async () => {
    if (!selectedContact) return;
    setContactSaving(true);
    setContactSaveMessage(null);
    const payload: UpdateContactPayload = {
      name: contactForm.name,
      contact_name: contactForm.contact_name,
      company_name: contactForm.company_name,
      contact_type: contactForm.contact_type,
      role_id: contactForm.role_id,
      crm_stage: contactForm.crm_stage,
      owner: contactForm.owner,
      email: contactForm.email,
      phone: contactForm.phone,
      website: contactForm.website,
      location_city: contactForm.location_city,
      location_region: contactForm.location_region,
      country: contactForm.country,
      address_line: contactForm.address_line,
      notes: contactForm.notes,
      tags: contactForm.tags
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean),
      source_urls: contactForm.source_url ? [contactForm.source_url] : [],
    };
    try {
      const updated = await updateContact(selectedContact.id, payload);
      setContactsState((prev) => ({
        ...prev,
        data: prev.data.map((contact) => (contact.id === updated.id ? updated : contact)),
      }));
      setContactForm(createContactEditorState(updated));
      setContactSaveMessage("Saved");
      push({
        title: "Contact updated",
        message: updated.name || updated.company_name || `Contact #${updated.id}`,
      });
      if (hitlApprovalId) {
        try {
          await approveHitlItem(hitlApprovalId, {
            name: updated.name ?? undefined,
            contact_name: updated.contact_name ?? undefined,
          });
          push({ title: "HITL approved", message: "Contact approved from HITL queue." });
          setHitlApprovalId(null);
        } catch (hitlError) {
          push({
            title: "HITL approval failed",
            message: hitlError instanceof Error ? hitlError.message : "Unable to approve HITL item.",
          });
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to update contact.";
      setContactSaveMessage(message);
      push({ title: "Update failed", message });
      handleCapabilityBlock(error, "CRM contact automation");
    } finally {
      setContactSaving(false);
    }
  };

  const toggleContactSelection = useCallback((contactId: number) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  const selectAllContacts = useCallback(() => {
    setSelectedContactIds(new Set(contactsState.data.map((contact) => contact.id)));
  }, [contactsState.data]);

  const clearContactSelection = useCallback(() => {
    setSelectedContactIds(new Set());
  }, []);

  const allContactsSelected =
    contactsState.data.length > 0 && selectedContactIds.size === contactsState.data.length;
  const someContactsSelected = selectedContactIds.size > 0 && !allContactsSelected;

  useEffect(() => {
    if (!masterSelectRef.current) return;
    masterSelectRef.current.indeterminate = someContactsSelected;
  }, [someContactsSelected]);

  const handleContactAction = async () => {
    if (selectedAction === "enrich_contacts") {
      setActionBusy(true);
      try {
        if (!selectedContactIds.size) {
          // No selection — enrich all contacts without an email
          await enrichAllCrmContacts();
          push({ title: "Enrichment started", message: "Running web search enrichment on all contacts missing an email." });
          await loadContacts(contactSearchRef.current, searchFilterRef.current, contactPageRef.current);
        } else {
          const contactIds = Array.from(selectedContactIds);
          const batchSize = 3;
          let succeeded = 0;
          let failed = 0;
          let firstError: unknown;
          for (let i = 0; i < contactIds.length; i += batchSize) {
            const batch = contactIds.slice(i, i + batchSize);
            const results = await Promise.allSettled(batch.map((contactId) => enrichCrmContact(contactId)));
            for (const result of results) {
              if (result.status === "fulfilled") {
                succeeded += 1;
              } else {
                failed += 1;
                if (!firstError) {
                  firstError = result.reason;
                }
              }
            }
          }
          if (succeeded) {
            push({
              title: "Enrichment queued",
              message: `Queued Serper enrichment for ${succeeded} contact${succeeded === 1 ? "" : "s"}.`,
            });
            await loadContacts(contactSearchRef.current, searchFilterRef.current, contactPageRef.current);
          }
          if (failed) {
            const message =
              failed === contactIds.length
                ? firstError instanceof Error
                  ? firstError.message
                  : "Unable to queue enrichment."
                : `${failed} contact${failed === 1 ? "" : "s"} failed to queue.`;
            push({ title: "Enrichment issues", message });
            handleCapabilityBlock(firstError ?? new Error("Enrichment failed"), "CRM enrichment");
          }
        }
      } finally {
        setActionBusy(false);
      }
      return;
    }
    if (selectedAction === "dedup_workspace") {
      if (!canUseCrmDedupe) {
        openUpgradeModal("CRM dedupe");
        return;
      }
      setDedupWorkspaceOpen(true);
      void loadDedupQueue();
      return;
    }
    if (selectedAction === "dedupe_contacts") {
      if (!canUseCrmDedupe) {
        openUpgradeModal("CRM dedupe");
        return;
      }
      setActionBusy(true);
        try {
          await runDeduplication({ source: "crm", limit: 1000 });
          push({ title: "Dedup queued", message: "CRM dedupe scan started. Open Dedup Workspace to review." });
          setDedupWorkspaceOpen(true);
        } catch (error) {
          push({ title: "Dedup failed", message: error instanceof Error ? error.message : "Unable to run dedupe." });
          handleCapabilityBlock(error, "CRM dedupe");
        } finally {
          setActionBusy(false);
        }
      return;
    }
    if (selectedAction === "delete_contacts") {
      if (!selectedContactIds.size) {
        push({ title: "Select contacts", message: "Choose one or more contacts first." });
        return;
      }
      setDeleteConfirmOpen(true);
      return;
    }
    if (!selectedContact) {
      push({ title: "Select a contact", message: "Pick a contact first." });
      return;
    }
    if (selectedAction === "send_email") {
      setEmailDrawerContact(selectedContact);
      setEmailDrawerLead(leadsForContact[0] ?? null);
      setEmailDrawerOpen(true);
      return;
    }
    if (selectedAction === "create_lead") {
      setActionBusy(true);
      try {
        const title =
          selectedContact.company_name ||
          selectedContact.name ||
          selectedContact.location_city ||
          "New routing opportunity";
        const payload = await createLead({
          contact_id: selectedContact.id,
          title,
          source: "CRM",
          status: "open",
          pipeline_stage: "new",
          region: selectedContact.location_region || undefined,
          description: selectedContact.notes || undefined,
        });
        setLeadsState((prev) => ({ ...prev, data: [payload, ...prev.data] }));
        push({
          title: "Lead created",
          message: `${title} → booking kanban.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to create lead.";
        push({ title: "Lead creation failed", message });
        handleCapabilityBlock(error, "CRM lead automation");
      } finally {
        setActionBusy(false);
      }
      return;
    }
    const actionLabel = CONTACT_ACTIONS.find((action) => action.id === selectedAction)?.label ?? "Action";
    push({
      title: actionLabel,
      message: `${actionLabel} queued for ${selectedContact.name || selectedContact.company_name || `Contact #${selectedContact.id}`}`,
    });
  };

  const handleConfirmDeleteContacts = async () => {
    const contactIds = Array.from(selectedContactIds);
    if (!contactIds.length) {
      setDeleteConfirmOpen(false);
      return;
    }
    setDeleteBusy(true);
    const deletedContacts = contactsState.data.filter((contact) => contactIds.includes(contact.id));
    try {
      const results = await Promise.allSettled(contactIds.map((contactId) => deleteContact(contactId)));
      const failed = results.filter((result) => result.status === "rejected");
      const succeededIds = contactIds.filter((_, index) => results[index].status === "fulfilled");

      if (succeededIds.length) {
        setContactsState((prev) => ({
          ...prev,
          data: prev.data.filter((contact) => !succeededIds.includes(contact.id)),
          total: Math.max(0, prev.total - succeededIds.length),
        }));
        setSelectedContactIds((prev) => {
          const next = new Set(prev);
          succeededIds.forEach((id) => next.delete(id));
          return next;
        });
        if (selectedContactId !== null && succeededIds.includes(selectedContactId)) {
          const remaining = contactsState.data.filter((contact) => !succeededIds.includes(contact.id));
          setSelectedContactId(remaining[0]?.id ?? null);
          setContactDrawerOpen(false);
        }
        push({
          title: "Contacts deleted",
          message: `Removed ${succeededIds.length} contact${succeededIds.length === 1 ? "" : "s"}.`,
        });
        if (deletedContacts.length) {
          if (actionUndo?.timeoutId) clearTimeout(actionUndo.timeoutId);
          const timeoutId = window.setTimeout(() => {
            setActionUndo(null);
          }, 8000);
          setActionUndo({ type: "delete", contacts: deletedContacts, timeoutId });
        }
      }
      if (failed.length) {
        const message =
          failed.length === results.length
            ? (failed[0] as PromiseRejectedResult).reason instanceof Error
              ? (failed[0] as PromiseRejectedResult).reason.message
              : "Unable to delete contacts."
            : `${failed.length} contact${failed.length === 1 ? "" : "s"} failed to delete.`;
        push({ title: "Delete issues", message });
        handleCapabilityBlock((failed[0] as PromiseRejectedResult).reason, "CRM delete contacts");
      }
    } finally {
      setDeleteBusy(false);
      setDeleteConfirmOpen(false);
    }
  };

  useEffect(() => {
    if (!urlReady) return;
    void loadContacts(contactSearchRef.current, searchFilterRef.current, 1, true);
  }, [sortKey, sortOrder, loadContacts, urlReady]);

  useEffect(() => {
    const handleMove = (event: MouseEvent) => {
      if (!resizingRef.current) return;
      const { key, startX, startWidth } = resizingRef.current;
      const next = Math.max(120, startWidth + (event.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [key]: next }));
    };
    const handleUp = () => {
      resizingRef.current = null;
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, []);

  const contactsListWidth = useMemo(() => {
    return Object.values(columnWidths).reduce((sum, value) => sum + value, 16);
  }, [columnWidths]);

  const contactsTableRef = useRef<HTMLDivElement | null>(null);
  const [contactsViewportWidth, setContactsViewportWidth] = useState(0);

  useEffect(() => {
    const node = contactsTableRef.current;
    if (!node) return;
    const updateWidth = () => {
      setContactsViewportWidth(node.clientWidth);
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const listWidth = Math.max(contactsListWidth, contactsViewportWidth || 0);

  const ContactRow = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const contact = contactsState.data[index];
      if (!contact) return null;
      const isActive = contact.id === selectedContact?.id;
      const isChecked = selectedContactIds.has(contact.id);
      const isEditing = inlineEditId === contact.id;
      const location = [contact.location_city, contact.location_region, contact.country]
        .filter(Boolean)
        .join(", ");
      const rawUrl = contact.website ?? contact.source_url ?? contact.source_urls?.[0] ?? "";
      const urlLabel = rawUrl === "email.inbound" ? "" : rawUrl;
      return (
        <div style={style} className="border-b border-gray-900/60">
          <div className="flex items-start gap-2 px-2 py-1">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-primary"
              checked={isChecked}
              onChange={() => toggleContactSelection(contact.id)}
            />
            <div
              className={`flex-1 text-left text-sm transition ${
                isActive ? "bg-primary/10 text-foreground" : "hover:bg-background/40 text-foreground/80"
              }`}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedContactId(contact.id)}
              onDoubleClick={() => {
                setSelectedContactId(contact.id);
                setContactDrawerOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setSelectedContactId(contact.id);
                }
              }}
            >
              {isEditing && inlineDraft ? (
                <div className="flex items-center gap-3 text-sm" style={{ minWidth: contactsListWidth }}>
                  <input
                    type="text"
                    className="rounded-md border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                    style={{ width: columnWidths.name }}
                    value={inlineDraft.name}
                    onChange={(event) =>
                      setInlineDraft((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                    }
                  />
                  <span className="truncate text-foreground/70" style={{ width: columnWidths.location }}>
                    {location || "Location TBA"}
                  </span>
                  <input
                    type="email"
                    className="rounded-md border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                    style={{ width: columnWidths.email }}
                    value={inlineDraft.email}
                    onChange={(event) =>
                      setInlineDraft((prev) => (prev ? { ...prev, email: event.target.value } : prev))
                    }
                  />
                  <input
                    type="text"
                    className="rounded-md border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                    style={{ width: columnWidths.url }}
                    value={inlineDraft.url}
                    onChange={(event) =>
                      setInlineDraft((prev) => (prev ? { ...prev, url: event.target.value } : prev))
                    }
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3 text-sm text-foreground/70" style={{ minWidth: contactsListWidth }}>
                  <span
                    className={`min-w-0 truncate text-base font-semibold ${
                      isActive ? "text-foreground" : "text-foreground"
                    }`}
                    style={{ width: columnWidths.name }}
                  >
                    {contact.name || contact.company_name || "Untitled contact"}
                  </span>
                  <span
                    className={`truncate ${isActive ? "text-foreground/80" : "text-foreground/70"}`}
                    style={{ width: columnWidths.location }}
                  >
                    {location || "Location TBA"}
                  </span>
                  <span
                    className={`truncate ${
                      isActive ? "text-foreground" : "text-foreground"
                    }`}
                    style={{ width: columnWidths.email }}
                  >
                    {contact.email || "No email"}
                  </span>
                  <span className="truncate text-foreground/70" style={{ width: columnWidths.url }}>
                    {urlLabel}
                    {hitlByContactId.get(contact.id) ? " • Pending HITL" : ""}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-0.5 pt-0.5">
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full p-1"
                    onClick={() => void saveInlineEdit()}
                    disabled={inlineSaving}
                    aria-label="Save inline edit"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full p-1"
                    onClick={cancelInlineEdit}
                    aria-label="Cancel inline edit"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-full px-2.5 py-1 text-[11px] font-semibold"
                    onClick={() => startInlineEdit(contact)}
                    aria-label="Quick edit"
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full p-1"
                    onClick={() => handleQuickEmail(contact)}
                    aria-label="Email contact"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full p-1"
                    onClick={() => handleCopy("email", contact.email)}
                    aria-label="Copy email"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-full p-1"
                    onClick={() => void handleQuickEnrich(contact)}
                    aria-label="Enrich contact"
                  >
                    <Sparkles className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      contactsState.data,
      selectedContact,
      selectedContactIds,
      inlineEditId,
      inlineDraft,
      inlineSaving,
      contactsListWidth,
      columnWidths,
      hitlByContactId,
      toggleContactSelection,
      startInlineEdit,
      cancelInlineEdit,
      saveInlineEdit,
      handleQuickEmail,
      handleQuickEnrich,
      handleCopy,
    ],
  );

  return (
    <div className="space-y-4">
      <section className="space-y-4">
        <div className="aa-glass-card rounded-xl p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="aa-page-title text-lg font-semibold">Contacts</h2>
              <p className="text-xs text-muted-foreground">Search synced contacts and review associated leads.</p>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Total contacts:{" "}
                <span className="font-semibold text-foreground">
                  {contactsState.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-8 md:w-8"
                onClick={() => void loadContacts(contactSearch, searchFilter, 1, true)}
                disabled={contactsState.loading}
                aria-label="Refresh contacts"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {(undoEdit || actionUndo) && (
                <div className="flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[11px] uppercase tracking-wide text-foreground">
                  <span>
                    {actionUndo?.type === "delete"
                      ? "Contacts deleted"
                      : actionUndo?.type === "merge"
                        ? "Merge applied"
                        : "Change saved"}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full px-3 py-1 text-[11px] uppercase tracking-wide"
                    onClick={() => {
                      if (actionUndo) {
                        void (async () => {
                          if (actionUndo.type === "delete") {
                            const payloads = actionUndo.contacts.map((contact) => ({
                              name: contact.name || contact.company_name || "Restored contact",
                              contact_name: contact.contact_name ?? undefined,
                              company_name: contact.company_name ?? undefined,
                              email: contact.email ?? undefined,
                              phone: contact.phone ?? undefined,
                              website: contact.website ?? undefined,
                              contact_type: contact.contact_type ?? undefined,
                              role_id: contact.role_id ?? undefined,
                              tags: contact.tags ?? undefined,
                              source_url: contact.source_url ?? undefined,
                              notes: contact.notes ?? undefined,
                            }));
                            await Promise.all(payloads.map((payload) => createContact(payload)));
                            push({ title: "Contacts restored", message: `${payloads.length} contact(s) recreated.` });
                            void loadContacts(contactSearchRef.current, searchFilterRef.current, 1, true);
                          }
                          if (actionUndo.type === "merge") {
                            await updateContact(actionUndo.primaryPrev.id, {
                              name: actionUndo.primaryPrev.name ?? undefined,
                              contact_name: actionUndo.primaryPrev.contact_name ?? undefined,
                              company_name: actionUndo.primaryPrev.company_name ?? undefined,
                              email: actionUndo.primaryPrev.email ?? undefined,
                              phone: actionUndo.primaryPrev.phone ?? undefined,
                              website: actionUndo.primaryPrev.website ?? undefined,
                              location_city: actionUndo.primaryPrev.location_city ?? undefined,
                              location_region: actionUndo.primaryPrev.location_region ?? undefined,
                              country: actionUndo.primaryPrev.country ?? undefined,
                              address_line: actionUndo.primaryPrev.address_line ?? undefined,
                              owner: actionUndo.primaryPrev.owner ?? undefined,
                              crm_stage: actionUndo.primaryPrev.crm_stage ?? undefined,
                              tags: actionUndo.primaryPrev.tags ?? undefined,
                              notes: actionUndo.primaryPrev.notes ?? undefined,
                              source_url: actionUndo.primaryPrev.source_url ?? undefined,
                            });
                            await Promise.all(
                              actionUndo.secondaryPrev.map((secondary) =>
                                updateContact(secondary.id, { notes: secondary.notes ?? undefined }),
                              ),
                            );
                            push({ title: "Merge reverted", message: "Primary contact restored." });
                            void loadContacts(contactSearchRef.current, searchFilterRef.current, 1, true);
                          }
                          if (actionUndo.timeoutId) clearTimeout(actionUndo.timeoutId);
                          setActionUndo(null);
                        })();
                      } else {
                        void undoInlineEdit();
                      }
                    }}
                  >
                    Undo
                  </Button>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full px-3 py-1.5 font-semibold"
                onClick={() => setCreateContactDrawerOpen(true)}
              >
                Create contact
              </Button>
              <label className="flex items-center gap-2 rounded-full border border-border bg-background/40 px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>Action</span>
                <select
                  className="bg-transparent text-sm text-foreground outline-none"
                  value={selectedAction}
                  onChange={(event) => setSelectedAction(event.target.value as (typeof CONTACT_ACTIONS)[number]["id"])}
                >
                  {CONTACT_ACTIONS.map((action) => (
                    <option key={action.id} value={action.id}>
                      {action.label}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full px-4 py-1.5 text-xs font-semibold"
                onClick={() => void handleContactAction()}
                disabled={
                  actionBusy ||
                  (selectedAction === "delete_contacts"
                    ? !selectedContactIds.size
                    : selectedAction === "dedupe_contacts" || selectedAction === "dedup_workspace"
                      ? false
                      : !selectedContact)
                }
              >
                {actionBusy ? "Working…" : "Run action"}
              </Button>
            </div>
        </div>
        {dedupStatus && (
            <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              {dedupStatus === "queued"
                ? "Dedup scan queued."
                : dedupStatus === "failed"
                  ? "Scan failed. Try again in a moment."
                  : "Scanning for duplicates…"}
            </p>
          )}
          <div className="mt-2 flex flex-col gap-2 text-[11px] uppercase tracking-wide text-muted-foreground sm:flex-row sm:items-end sm:gap-3">
            <label className="flex min-h-[44px] md:min-h-[36px] items-center gap-2 text-[11px] uppercase tracking-wide text-foreground/70">
              <input
                ref={masterSelectRef}
                type="checkbox"
                className="h-5 w-5 accent-primary md:h-4 md:w-4"
                checked={allContactsSelected}
                onChange={(event) => {
                  if (event.target.checked) {
                    selectAllContacts();
                  } else {
                    clearContactSelection();
                  }
                }}
                disabled={!contactsState.data.length}
              />
              <span>Select all on page</span>
            </label>
            <label className="flex flex-1 items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={searchFilter}
                onChange={(event) => {
                  const nextFilter = event.target.value as SearchFilter;
                  setSearchFilter(nextFilter);
                  void loadContacts(contactSearch, nextFilter, 1, true);
                }}
                className="flex-1 rounded-xl border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
              >
                {SEARCH_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </label>
            {showSearchInputFilter && (
              <div className="flex w-full items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  className="w-full rounded-xl border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                  placeholder={activeSearchFilter?.placeholder ?? "Search contacts"}
                  value={contactSearch}
                  onChange={(event) => setContactSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void loadContacts(contactSearch, searchFilter, 1, true);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl px-3 py-2 text-sm font-semibold"
                  onClick={() => void loadContacts(contactSearch, searchFilter, 1, true)}
                  disabled={contactsState.loading}
                >
                  Search
                </Button>
              </div>
            )}
            {showStateDropdown && (
              <div className="flex flex-1 flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">State</span>
                <div className="flex gap-2">
                  <select
                    value={stateFilter}
                    onChange={(event) => setStateFilter(event.target.value)}
                    className="flex-1 rounded-xl border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  >
                    <option value="">All states</option>
                    {STATE_OPTIONS.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full px-3 py-2 text-[11px] uppercase tracking-wide"
                  onClick={() => setStateFilter("")}
                >
                  Clear state
                </Button>
                </div>
              </div>
            )}
            {showTypeDropdown && (
              <div className="flex flex-1 flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Type</span>
                  <select
                    value={contactTypeFilter}
                    onChange={(event) => setContactTypeFilter(event.target.value as "all" | "artists" | "venues")}
                    className="rounded-xl border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                  >
                  <option value="all">All contacts</option>
                  <option value="artists">Artists</option>
                  <option value="venues">Venues</option>
                </select>
              </div>
            )}
            <div className="flex flex-1 items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-1 gap-2">
                <select
                  value={sortKey}
                  onChange={(event) => setSortKey(event.target.value as SortKey)}
                  className="flex-1 rounded-xl border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                >
                  {SORT_KEYS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full px-3 py-2 text-[11px] uppercase tracking-wide"
                  onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
                >
                  {sortOrder === "asc" ? "Ascending" : "Descending"}
                </Button>
              </div>
            </div>
          </div>
          {contactsState.error && <p className="mt-2 text-sm text-rose-400">{contactsState.error}</p>}
          <div className="mt-2 space-y-2 md:hidden">
            {contactsLoading && !contactsState.data.length ? (
              <div className="rounded-xl border border-border bg-background/40 p-4 text-sm text-muted-foreground">
                Loading contacts…
              </div>
            ) : contactsState.data.length ? (
              contactsState.data.map((contact) => {
                const isChecked = selectedContactIds.has(contact.id);
                const location = [contact.location_city, contact.location_region, contact.country]
                  .filter(Boolean)
                  .join(", ");
                const rawUrl = contact.website ?? contact.source_url ?? contact.source_urls?.[0] ?? "";
                const urlLabel = rawUrl === "email.inbound" ? "" : rawUrl;
                return (
                  <div
                    key={contact.id}
                    className="rounded-xl border border-border bg-background/40 p-3 space-y-3"
                    onClick={() => {
                      setSelectedContactId(contact.id);
                      setContactDrawerOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        setSelectedContactId(contact.id);
                        setContactDrawerOpen(true);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <label
                        className="inline-flex min-h-[44px] md:min-h-[36px] min-w-[44px] md:min-h-[36px] md:min-w-[36px] items-center justify-center"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleContactSelection(contact.id)}
                          className="h-5 w-5 accent-primary md:h-4 md:w-4"
                        />
                        <span className="sr-only">Select contact</span>
                      </label>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">
                          {contact.name || contact.company_name || "Untitled contact"}
                        </p>
                        <p className="text-xs text-muted-foreground">{location || "Location TBA"}</p>
                        <p className="text-xs text-muted-foreground">{contact.email || "No email"}</p>
                        <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {urlLabel}
                          {hitlByContactId.get(contact.id) ? " • Pending HITL" : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full px-3 text-xs font-semibold"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedContactId(contact.id);
                          setContactDrawerOpen(true);
                        }}
                      >
                        Open
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full px-3 text-xs font-semibold"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleQuickEmail(contact);
                        }}
                      >
                        Email
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full px-3 text-xs font-semibold"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCopy("email", contact.email);
                        }}
                      >
                        Copy email
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full px-3 text-xs font-semibold"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCopy("phone", contact.phone);
                        }}
                      >
                        Copy phone
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full px-3 text-xs font-semibold"
                        onClick={(event) => {
                          event.stopPropagation();
                          void handleQuickEnrich(contact);
                        }}
                      >
                        Enrich
                      </Button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-xl border border-border bg-background/40 p-6 text-center">
                {contactSearch.trim() ? (
                  <p className="text-sm text-muted-foreground">No contacts match <span className="font-medium text-foreground">"{contactSearch.trim()}"</span>. Try a different search.</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">No contacts yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">Add your first venue or booking contact to get started.</p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="mt-3 h-auto px-0 text-xs font-medium"
                      onClick={() => setCreateContactDrawerOpen(true)}
                    >
                      + Add a contact
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div ref={contactsTableRef} className="mt-2 hidden rounded-xl border border-gray-900/60 md:block">
            <div className="w-full overflow-x-auto">
              <div
                className="flex items-center gap-3 border-b border-gray-900/60 bg-background/60 px-2 py-1 text-[11px] uppercase tracking-wide text-foreground/70"
                style={{ minWidth: contactsListWidth }}
              >
                <div className="w-4" />
                <div className="flex items-center gap-3">
                      {(
                        [
                          { key: "name", label: "Name" },
                          { key: "location", label: "Location" },
                          { key: "email", label: "Email" },
                          { key: "url", label: "URL" },
                        ] as const
                      ).map((column) => (
                        <div
                          key={column.key}
                          className="relative flex items-center gap-2"
                          style={{ width: columnWidths[column.key] }}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto px-0 text-left text-[11px] uppercase tracking-wide text-foreground/70 hover:text-foreground"
                        onClick={() => {
                          if (column.key === "url") return;
                          const nextSort = column.key === "location" ? "city" : (column.key as SortKey);
                          if (sortKey === nextSort) {
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                            return;
                          }
                          setSortKey(nextSort);
                          setSortOrder("asc");
                        }}
                      >
                        {column.label}
                        {(() => {
                          const nextSort = column.key === "location" ? "city" : (column.key as SortKey);
                          if (column.key === "url" || sortKey !== nextSort) return "";
                          return sortOrder === "asc" ? " ↑" : " ↓";
                        })()}
                      </Button>
                      <div
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                        onMouseDown={(event) => {
                          resizingRef.current = {
                            key: column.key,
                            startX: event.clientX,
                            startWidth: columnWidths[column.key],
                          };
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {contactsLoading && !contactsState.data.length ? (
              <div className="px-3 py-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="mb-2 h-8 w-full rounded-lg bg-muted/40 animate-pulse" />
                ))}
              </div>
            ) : contactsState.data.length ? (
              <div className="w-full overflow-x-auto">
                <List
                  height={listHeight}
                  width={listWidth}
                  itemCount={contactsState.data.length}
                  itemSize={CONTACT_ROW_HEIGHT}
                  overscanCount={6}
                >
                  {ContactRow}
                </List>
              </div>
            ) : (
              <div className="p-8 text-center">
                {contactSearch.trim() ? (
                  <p className="text-sm text-muted-foreground">No contacts match <span className="font-medium text-foreground">"{contactSearch.trim()}"</span>. Try a different search.</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">No contacts yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">Add your first venue or booking contact to get started.</p>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="mt-3 h-auto px-0 text-xs font-medium"
                      onClick={() => setCreateContactDrawerOpen(true)}
                    >
                      + Add a contact
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-wide text-muted-foreground">
            <div className="flex items-center gap-2">
              <span>Page</span>
              <input
                type="text"
                inputMode="numeric"
                className="w-14 rounded-lg border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                value={contactPageInput}
                onChange={(event) => setContactPageInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitContactPageInput(contactPageInput);
                  }
                }}
                onBlur={() => {
                  commitContactPageInput(contactPageInput);
                }}
                aria-label="Contact page number"
              />
              <span>
                of {totalContactPages}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(1)}
                disabled={contactPage <= 1}
                aria-label="First page"
                title="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(contactPage - 5)}
                disabled={contactPage <= 1}
                aria-label="Previous 5 pages"
                title="Previous 5 pages"
              >
                <Rewind className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(contactPage - 1)}
                disabled={contactPage <= 1}
                aria-label="Previous page"
                title="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(contactPage + 1)}
                disabled={contactPage >= totalContactPages}
                aria-label="Next page"
                title="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(contactPage + 5)}
                disabled={contactPage >= totalContactPages}
                aria-label="Next 5 pages"
                title="Next 5 pages"
              >
                <FastForward className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full md:h-9 md:w-9"
                onClick={() => goToContactPage(totalContactPages)}
                disabled={contactPage >= totalContactPages}
                aria-label="Last page"
                title="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            {selectedContact
              ? `Selected: ${selectedContact.name || selectedContact.company_name || selectedContact.email || selectedContact.id}`
              : "No contact selected"}
          </div>
        </div>
      </section>
      <Drawer
        open={contactDrawerOpen}
        onOpenChange={(next) => {
          if (!next) {
            setContactDrawerOpen(false);
          }
        }}
      >
        <DrawerContent data-full>
          <DrawerHeader className="border-b border-gray-900/20">
            <div className="flex items-center justify-between gap-3">
              <DrawerTitle>Contact workspace</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost">Close</Button>
              </DrawerClose>
            </div>
            <DrawerDescription>
              {selectedContact ? `Contact Name: ${selectedContact.owner || "Unassigned"}` : "Select a contact to begin."}
            </DrawerDescription>
          </DrawerHeader>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="space-y-4 px-6 pb-24">
              <div className="w-full rounded-xl border border-border bg-background/40 p-3">
              <Tabs value={contactTab} onValueChange={(value) => setContactTab(value as "profile" | "timeline" | "history")}>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedContact && renderHitlPill(hitlByContactId.get(selectedContact.id), "md")}
                  <TabsList>
                    <TabsTrigger value="profile">Profile</TabsTrigger>
                    <TabsTrigger value="timeline">Activity</TabsTrigger>
                    <TabsTrigger value="history">History</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="profile">
                  {selectedContact ? (
                    <>
                      <CapabilityGate
                        flag="canUseCrmGraphLinks"
                        fallback={
                          <LockedFeatureCard
                            flag="canUseCrmGraphLinks"
                            title="Workflow links"
                            description="Upgrade to jump into routing, booking, and memory tools from CRM."
                            variant="inline"
                            className="text-[11px]"
                            onUpgrade={() => openUpgradeModal("CRM cross-links")}
                          />
                        }
                      >
                        <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {contactLinks.map((link) => (
                            <Link
                              key={link.label}
                              to={link.href}
                              className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs text-foreground hover:text-foreground"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      </CapabilityGate>
                      <form
                        className="w-full space-y-4"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void handleContactSave();
                        }}
                      >
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-2">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Display name
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.name}
                              onChange={(event) => handleContactFieldChange("name", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Company or venue
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.company_name}
                              onChange={(event) => handleContactFieldChange("company_name", event.target.value)}
                            />
                          </label>
                        </div>
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-2">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Contact type
                            <select
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.contact_type}
                              onChange={(event) => handleContactFieldChange("contact_type", event.target.value)}
                            >
                              {CONTACT_TYPES.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Role
                            <select
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.role_id}
                              onChange={(event) => handleContactFieldChange("role_id", event.target.value)}
                            >
                              {ROLE_OPTIONS.map((role) => (
                                <option key={role} value={role}>
                                  {role}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-3 lg:grid-cols-3">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            CRM stage
                            <select
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.crm_stage}
                              onChange={(event) => handleContactFieldChange("crm_stage", event.target.value)}
                            >
                              {STAGE_OPTIONS.map((stage) => (
                                <option key={stage} value={stage}>
                                  {stage}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Contact Name
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.owner}
                              onChange={(event) => handleContactFieldChange("owner", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Source URL
                            <input
                              type="url"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.source_url}
                              onChange={(event) => handleContactFieldChange("source_url", event.target.value)}
                            />
                          </label>
                        </div>
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-2">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Email
                            <input
                              type="email"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.email}
                              onChange={(event) => handleContactFieldChange("email", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Phone
                            <input
                              type="tel"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.phone}
                              onChange={(event) => handleContactFieldChange("phone", event.target.value)}
                            />
                          </label>
                        </div>
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-3">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Website
                            <input
                              type="url"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.website}
                              onChange={(event) => handleContactFieldChange("website", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            City
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.location_city}
                              onChange={(event) => handleContactFieldChange("location_city", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            State / Region
                            <select
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.location_region}
                              onChange={(event) => handleContactFieldChange("location_region", event.target.value)}
                            >
                              <option value="">Region</option>
                              {STATE_OPTIONS.map((state) => (
                                <option key={state} value={state}>
                                  {state}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="grid gap-3 text-sm text-foreground/70 sm:grid-cols-2 lg:grid-cols-3">
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Country
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.country}
                              onChange={(event) => handleContactFieldChange("country", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Address
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.address_line}
                              onChange={(event) => handleContactFieldChange("address_line", event.target.value)}
                            />
                          </label>
                          <label className="text-xs uppercase tracking-wide text-muted-foreground">
                            Tags (comma separated)
                            <input
                              type="text"
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-3 py-1.5 text-sm text-foreground"
                              value={contactForm.tags}
                              placeholder="festival, vip, approved"
                              onChange={(event) => handleContactFieldChange("tags", event.target.value)}
                            />
                          </label>
                        </div>
                        <label className="text-xs uppercase tracking-wide text-muted-foreground">
                          Notes
                          <textarea
                            className="mt-1 min-h-[5rem] w-full rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
                            value={contactForm.notes}
                            onChange={(event) => handleContactFieldChange("notes", event.target.value)}
                          />
                        </label>
                        {contactSaveMessage && (
                          <p className={`text-sm ${contactSaveMessage === "Saved" ? "text-emerald-300" : "text-rose-300"}`}>{contactSaveMessage}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            type="submit"
                            variant="success"
                            className="rounded-full px-4 py-2 text-sm font-semibold"
                            disabled={contactSaving}
                          >
                            {contactSaving ? "Saving…" : "Save contact"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-full px-4 py-2 text-sm"
                            onClick={() => {
                              setEmailDrawerContact(selectedContact);
                              setEmailDrawerLead(leadsForContact[0] ?? null);
                              setEmailDrawerOpen(true);
                            }}
                          >
                            Email contact
                          </Button>
                        </div>
                      </form>
                      <div className="mt-4 w-full grid gap-3 rounded-2xl border border-border bg-background/50 p-4">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">AI Assist</p>
                            <p className="text-sm font-semibold text-foreground">Accelerate contact updates</p>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-full px-3 py-1 text-xs font-semibold"
                            onClick={() => {
                              if (!canUseCrmInsights) {
                                openUpgradeModal("CRM insights");
                                return;
                              }
                              setShowInsights((prev) => !prev);
                            }}
                          >
                            {showInsights ? "Hide insights" : "Show insights"}
                          </Button>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-full px-3 py-1 font-semibold"
                            onClick={() => selectedContact && void handleQuickEnrich(selectedContact)}
                          >
                            <Sparkles className="mr-2 inline h-3 w-3" />
                            Enrich contact
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-full px-3 py-1 font-semibold"
                            onClick={() => {
                              if (!selectedContact) return;
                              handleQuickEmail(selectedContact);
                            }}
                          >
                            <Mail className="mr-2 inline h-3 w-3" />
                            Draft intro email
                          </Button>
                        </div>
                        {showInsights && (
                          <div className="pt-2">
                            <CapabilityGate
                              flag="canUseCrmInsights"
                              fallback={
                                <LockedFeatureCard
                                  flag="canUseCrmInsights"
                                  title="CRM insights"
                                  description="Upgrade to unlock AI enrichment summaries and venue intelligence."
                                  variant="inline"
                                  onUpgrade={() => openUpgradeModal("CRM insights")}
                                />
                              }
                            >
                              <Suspense fallback={<p className="text-sm text-muted-foreground">Loading insights...</p>}>
                                <CRMInsightsPanel
                                  objectType="venue"
                                  objectId={selectedContact.id}
                                  objectName={
                                    selectedContact.name || selectedContact.company_name || `Contact #${selectedContact.id}`
                                  }
                                />
                              </Suspense>
                            </CapabilityGate>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Select a contact to edit details.</p>
                  )}
                </TabsContent>
                <TabsContent value="timeline">
                  <div className="rounded-xl border border-gray-900/70 bg-background/40 p-4">
                    {timelineLoading ? (
                      <p className="text-sm text-muted-foreground">Loading timeline…</p>
                    ) : (
                      <>
                        {timelineError && <p className="text-xs text-rose-400">{timelineError}</p>}
                        <TimelineSummary entries={timelineEntries} emptyLabel="No recent activity." />
                      </>
                    )}
                  </div>
                </TabsContent>
                <TabsContent value="history">
                  <div className="rounded-xl border border-gray-900/70 bg-background/40 p-4">
                    {activityLoading ? (
                      <p className="text-sm text-muted-foreground">Loading history…</p>
                    ) : (
                      <>
                        {activityError && <p className="text-xs text-rose-400">{activityError}</p>}
                        {activityLogs.length ? (
                          <ul className="space-y-3 text-sm text-foreground/80">
                            {activityLogs.map((log) => (
                              <li key={log.id} className="rounded-lg border border-border/60 bg-background/40 p-3">
                                <div className="flex items-center justify-between gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                                  <span>{log.action_type.replace(/_/g, " ")}</span>
                                  <span>{new Date(log.created_at).toLocaleString()}</span>
                                </div>
                                <p className="mt-2 text-sm text-foreground">
                                  {log.summary || "No summary provided."}
                                </p>
                                {log.agent_name && (
                                  <p className="mt-2 text-xs text-foreground/70">Agent: {log.agent_name}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">No activity history yet.</p>
                        )}
                      </>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={createContactDrawerOpen}
        onOpenChange={(open) => {
          setCreateContactDrawerOpen(open);
          if (!open) {
            setCreateContactInitial(null);
            setCreateContactLogId(null);
          }
        }}
      >
        <DrawerContent>
          <DrawerHeader className="border-b border-gray-900/20">
            <div className="flex items-center justify-between gap-3">
            <DrawerTitle>Create a contact</DrawerTitle>
              <DrawerClose asChild>
                <Button variant="ghost">Close</Button>
              </DrawerClose>
            </div>
            <DrawerDescription>Enter owner-approved contact details before linking to leads.</DrawerDescription>
          </DrawerHeader>
          <div className="px-6 pb-6">
            <ManualContactCard
              initialValues={createContactInitial ?? undefined}
              onSaved={async (created) => {
                if (created && createContactLogId) {
                  try {
                    await linkEmailContact(createContactLogId, { contact_id: created.id });
                  } catch (error) {
                    push({
                      title: "Link email",
                      message: error instanceof Error ? error.message : "Unable to link email to contact.",
                    });
                  }
                }
                void loadContacts(contactSearch, searchFilter, 1, true);
                setCreateContactDrawerOpen(false);
                setCreateContactInitial(null);
                setCreateContactLogId(null);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete selected contacts?</DialogTitle>
            <DialogDescription>
              Are you sure? This will delete all selected records.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)} disabled={deleteBusy}>
              No
            </Button>
            <Button variant="destructive" onClick={() => void handleConfirmDeleteContacts()} disabled={deleteBusy}>
              {deleteBusy ? "Deleting…" : "Yes, delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailDrawer
        open={emailDrawerOpen}
        onClose={handleEmailDrawerClose}
        contact={emailDrawerContact}
        lead={emailDrawerLead ?? undefined}
      />
      <Dialog open={dedupWorkspaceOpen} onOpenChange={setDedupWorkspaceOpen}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 overflow-hidden grid-rows-[auto,1fr,auto] gap-0">
          <DialogHeader className="border-b border-gray-900/20 p-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Dedup Workspace</p>
                <DialogTitle>Resolve duplicate contacts</DialogTitle>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-border px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  {dedupStatus === "running" ? "Scanning" : dedupGroups.length ? "Ready" : "Empty"}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full px-3 py-1 text-xs font-semibold"
                  onClick={() => void loadDedupQueue()}
                >
                  Refresh
                </Button>
                <DialogClose asChild>
                  <Button variant="ghost">Close</Button>
                </DialogClose>
              </div>
            </div>
            <DialogDescription>
              {dedupGroups.length
                ? `Group ${Math.min(dedupIndex + 1, dedupGroups.length)} of ${dedupGroups.length}`
                : "No duplicate groups found"}
            </DialogDescription>
            <div className="mt-3 flex items-center justify-end gap-2 text-xs text-muted-foreground">
              <Button
                type="button"
                variant={dedupView === "single" ? "secondary" : "outline"}
                size="sm"
                className="rounded-full px-3 py-1 text-xs font-semibold"
                onClick={() => setDedupView("single")}
              >
                Focus view
              </Button>
              <Button
                type="button"
                variant={dedupView === "table" ? "secondary" : "outline"}
                size="sm"
                className="rounded-full px-3 py-1 text-xs font-semibold"
                onClick={() => setDedupView("table")}
              >
                Table view
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-24 pt-4">
            {dedupLoading && (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                Loading dedup queue…
              </div>
            )}

            {!dedupLoading && dedupStatus === "queued" && !activeDedupGroup && (
              <div className="rounded-lg border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
                Scanning for duplicates… This usually takes a minute. We’ll auto-refresh when results are ready.
              </div>
            )}

            {!dedupLoading && !activeDedupGroup && (
              <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                No duplicate groups to review right now.
              </div>
            )}

            {!dedupLoading && activeDedupGroup && (
              <div className="space-y-6">
                <div className="grid gap-3 md:grid-cols-2">
                  {activeDedupGroup.contacts.map((contact) => {
                    const isPrimary = dedupSelection.primary === contact.id;
                    return (
                      <Button
                        key={contact.id}
                        type="button"
                        variant="ghost"
                        onClick={() => setDedupSelection((prev) => ({ ...prev, primary: contact.id }))}
                        className={`h-auto rounded-xl border px-4 py-3 text-left transition ${
                          isPrimary ? "border-primary bg-primary/10" : "border-border bg-background hover:bg-muted/30"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-foreground">{contact.label ?? `Contact #${contact.id}`}</p>
                            <p className="text-xs text-muted-foreground">
                              {contact.city ?? "—"} {contact.state ? `• ${contact.state}` : ""}
                            </p>
                          </div>
                          {isPrimary && (
                            <span className="rounded-full border border-primary/60 px-2 py-0.5 text-[10px] uppercase tracking-wide text-primary">
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground">
                          <span>{contact.email ?? "No email"}</span>
                          <span>{contact.phone ?? "No phone"}</span>
                          <span>{contact.website ?? "No website"}</span>
                        </div>
                        {dedupView === "single" && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {dedupFields.map((field) => {
                              const value = getDedupValue(contact, field.key);
                              if (!value) return null;
                              const selected = dedupSelection[field.key] === contact.id;
                              return (
                                <Button
                                  key={`${contact.id}-${field.key}`}
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDedupSelection((prev) => ({ ...prev, [field.key]: contact.id }))}
                                  className={`flex max-w-[220px] items-center gap-1 rounded-full px-2.5 py-1 text-[11px] transition ${
                                    selected
                                      ? "border-primary bg-primary/20 text-foreground"
                                      : "border-border text-muted-foreground hover:bg-muted/40"
                                  }`}
                                  title={`${field.label}: ${value}`}
                                >
                                  <span className="uppercase tracking-wide">{field.label}</span>
                                  <span className="truncate text-foreground">{value}</span>
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </Button>
                    );
                  })}
                </div>

                {dedupView === "table" && (
                  <div className="rounded-xl border border-border bg-muted/10 p-4">
                    <div className="grid gap-3">
                      {dedupFields.map((field) => {
                        const options = activeDedupGroup.contacts
                          .map((contact) => ({
                            id: contact.id,
                            label: contact.label ?? `Contact #${contact.id}`,
                            value: getDedupValue(contact, field.key),
                          }))
                          .filter((option) => option.value);
                        if (!options.length) return null;
                        return (
                          <div key={field.key} className="flex flex-wrap items-center justify-between gap-3">
                            <div className="min-w-[120px] text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                              {field.label}
                            </div>
                            <select
                              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                              value={dedupSelection[field.key] ?? ""}
                              onChange={(event) =>
                                setDedupSelection((prev) => ({
                                  ...prev,
                                  [field.key]: Number(event.target.value),
                                }))
                              }
                            >
                              {options.map((option) => (
                                <option key={`${field.key}-option-${option.id}`} value={option.id}>
                                  {option.value} — {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <div className="text-xs text-muted-foreground">
              {activeDedupGroup?.auto_merge_candidate ? "Auto-merge candidate detected" : "Manual review"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full px-4 py-2 text-xs font-semibold"
                onClick={() => void resolveDedupGroup("skip")}
                disabled={!activeDedupGroup}
              >
                Not duplicate
              </Button>
              <Button
                type="button"
                variant="success"
                size="sm"
                className="rounded-full px-4 py-2 text-xs font-semibold"
                onClick={() => void resolveDedupGroup("merge")}
                disabled={!activeDedupGroup}
              >
                Merge & Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <HitlDetailDrawer
        open={Boolean(activeHitlId)}
        hitlId={activeHitlId}
        item={activeHitlItem ?? undefined}
        onClose={closeHitlDrawer}
        onActionComplete={() => void loadHitlQueue()}
        defaultCategory="crm"
      />
      <CapabilityGate flag="canUseCrmDedupe" fallback={null}>
        <MergeContactDrawer
          open={mergeDrawerOpen}
          contacts={contactsState.data.filter((contact) => selectedContactIds.has(contact.id)).slice(0, 2)}
          onClose={() => setMergeDrawerOpen(false)}
          onMerge={handleMergeContacts}
        />
      </CapabilityGate>
    </div>
  );
}
